import { Popover as PopoverPrimitive, Slot } from "radix-ui"
import { createContext, useContext } from "react"
import type { ComponentProps } from "react"
import { Drawer as DrawerPrimitive } from "vaul"
import { cn } from "cn"

import { useIsMobile } from "../../../hooks/use-mobile"
import { DrawerContent } from "../drawer/drawer"

/**
 * Figma sieht für den Popover kein eigenes Mobile-Design vor. Unterhalb des
 * `md`-Breakpoints wird daher auf den bereits Figma-verifizierten `Drawer`
 * umgeschaltet (Bottom-Sheet statt schwebendem Panel). Die restlichen
 * Popover-Bausteine (`PopoverBody`, `PopoverFooter`) bleiben dabei identisch.
 */
const PopoverIsMobileContext = createContext(false)

function Popover({
  children,
  modal,
  ...props
}: ComponentProps<typeof PopoverPrimitive.Root>) {
  const isMobile = useIsMobile()

  return (
    <PopoverIsMobileContext.Provider value={isMobile}>
      {isMobile ? (
        // Radix-`modal` nicht an den Drawer durchreichen: `modal={false}` blendet
        // bei vaul das Overlay aus und lässt Klicks auf die Seite durch.
        <DrawerPrimitive.Root data-slot="popover" {...props}>
          {children}
        </DrawerPrimitive.Root>
      ) : (
        <PopoverPrimitive.Root data-slot="popover" modal={modal} {...props}>
          {children}
        </PopoverPrimitive.Root>
      )}
    </PopoverIsMobileContext.Provider>
  )
}

function PopoverTrigger({
  ...props
}: ComponentProps<typeof PopoverPrimitive.Trigger>) {
  const isMobile = useContext(PopoverIsMobileContext)

  return isMobile ? (
    <DrawerPrimitive.Trigger data-slot="popover-trigger" {...props} />
  ) : (
    <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
  )
}

/** Ohne Entsprechung im Drawer (Bottom-Sheet richtet sich nie an einem Anker aus) — auf Mobile daher nur strukturelles Passthrough. */
function PopoverAnchor({
  asChild,
  ...props
}: ComponentProps<typeof PopoverPrimitive.Anchor>) {
  const isMobile = useContext(PopoverIsMobileContext)

  if (isMobile) {
    const Comp = asChild ? Slot.Root : "div"
    return <Comp data-slot="popover-anchor" {...props} />
  }

  return (
    <PopoverPrimitive.Anchor
      data-slot="popover-anchor"
      asChild={asChild}
      {...props}
    />
  )
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  children,
  ...props
}: ComponentProps<typeof PopoverPrimitive.Content>) {
  const isMobile = useContext(PopoverIsMobileContext)

  if (isMobile) {
    return (
      <DrawerContent
        data-slot="popover-content"
        className={cn("items-stretch", className)}
      >
        <div
          data-slot="popover-handle"
          className="mx-auto h-1 w-9 shrink-0 rounded-sm bg-neutrals-v2-300"
        />
        {children}
      </DrawerContent>
    )
  }

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 flex w-75 flex-col gap-5 overflow-hidden rounded-xl border border-neutrals-v2-200 bg-backgrounds-primary shadow-popover outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          className
        )}
        {...props}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

/**
 * Figma „Content Area" (Default-Variante: `p-4` rundum). `PopoverContent`
 * selbst bleibt padding-los — bei Verwendung mit `PopoverFooter` hat der
 * Bereich in Figma stattdessen `pt-5` und keine Seiten-/Unterpolsterung
 * (Footer bringt eigenes Padding mit), per `className`-Override anpassbar.
 */
function PopoverBody({ className, ...props }: ComponentProps<"div">) {
  return (
    <div data-slot="popover-body" className={cn("p-4", className)} {...props} />
  )
}

/**
 * Figma „Footer" — nur in der Variante „mit Footer" vorhanden. Bringt
 * eigenes Padding, Radius und Hintergrund mit; die Buttons selbst sind
 * `children` (z. B. `Button` aus `@e2n/ui/v2`), keine festen Props.
 */
function PopoverFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="popover-footer"
      className={cn(
        "flex items-center justify-end gap-2 rounded-b-xl bg-backgrounds-primary px-5 pb-5",
        className
      )}
      {...props}
    />
  )
}

export {
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
}
